import {
  Channel,
  FlightRoomSummary,
  FlightRoomUser,
  FlightRoomUserStatus,
  FlightStatus,
  JobTitle,
  Task,
} from '@connect-x-app/shared-domain-models';

import { DateTime, getNow, getYesterday, isBetween, parseDate } from '../date-time';

const CREW_ROLE_ORDER: JobTitle[] = [
  JobTitle.COMM,
  JobTitle.COPILOT,
  JobTitle.SCCM,
  JobTitle.CCM,
  JobTitle.ACM,
  JobTitle.GH,
  JobTitle.DEADHEAD,
  JobTitle.UNKNOWN,
];

const formatAirport = (
  preferredAirportCode: string | null | undefined,
  backupAirportCode: string | null | undefined,
  combine: boolean,
): string => {
  if (preferredAirportCode && backupAirportCode && combine) {
    return `${preferredAirportCode}-${backupAirportCode}`;
  }

  if (preferredAirportCode?.trim()) {
    return preferredAirportCode;
  }

  if (backupAirportCode?.trim()) {
    return backupAirportCode;
  }

  return '–';
};

export const getFlightroomAirportNames = (
  flightroom: FlightRoomSummary | null,
  combine = false,
): {
  arrivalAirport: string;
  departureAirport: string;
} => {
  if (!flightroom) {
    return {
      arrivalAirport: '',
      departureAirport: '',
    };
  }
  const departureAirport = formatAirport(flightroom.departure.airport.iata, flightroom.departure.airport.icao, combine);
  const arrivalAirport = formatAirport(flightroom.destination.airport.iata, flightroom.destination.airport.icao, combine);
  return { arrivalAirport, departureAirport };
};

export const isRoomLanded = (room: FlightRoomSummary) => {
  return isLanded(room.schedule.sta, room.schedule.eta ?? null, room.schedule.ata ?? null);
};

export const isLanded = (sta: DateTime, eta?: DateTime | null, ata?: DateTime | null) => {
  const today = getNow();
  const yesterday = getYesterday();
  const arrivalDate = parseDate(ata ?? eta ?? sta);
  return isBetween(arrivalDate, yesterday, today);
};

export const checkIsUserScheduledForChannel = (externalUserId: string, channel: Channel): boolean => {
  return channel.users?.find(user => user.externalId === externalUserId)?.status === FlightRoomUserStatus.SCHEDULED;
};

export const isActiveMemberOfRoom = (room: FlightRoomSummary): boolean => {
  return room.userStatus !== undefined && room.userStatus !== FlightRoomUserStatus.PENDING;
};

export const isPartOfRoom = (room: FlightRoomSummary): boolean => {
  return room.userStatus !== undefined;
};

export const hasPendingAccessRequest = (room: FlightRoomSummary): boolean => {
  return room.userStatus === FlightRoomUserStatus.PENDING;
};

const isScheduled = ({ status }: FlightRoomUser): boolean => status === FlightRoomUserStatus.SCHEDULED;

const getCrewRoleOrder = (user: FlightRoomUser): number => {
  if (!isScheduled(user)) {
    return CREW_ROLE_ORDER.length;
  }

  const index = CREW_ROLE_ORDER.indexOf(user.jobTitle);
  return index === -1 ? CREW_ROLE_ORDER.indexOf(JobTitle.UNKNOWN) : index;
};

const getNameWithinRole = (user: FlightRoomUser): string =>
  isScheduled(user) ? (user.jobTitleCustom ?? user.jobTitle) : user.jobTitle;

const compareFlightroomUsersByRole = (u1: FlightRoomUser, u2: FlightRoomUser): number => {
  const roleOrderDifference = getCrewRoleOrder(u1) - getCrewRoleOrder(u2);
  if (roleOrderDifference !== 0) {
    return roleOrderDifference;
  }

  return getNameWithinRole(u1).localeCompare(getNameWithinRole(u2));
};

export const sortFlightroomUsersByRole = (users: FlightRoomUser[]): FlightRoomUser[] =>
  [...users].sort(compareFlightroomUsersByRole);

export const sortFlightroomsByStd = (f1: FlightRoomSummary, f2: FlightRoomSummary): number => {
  const f1Std = new Date(f1.schedule.std).getTime();
  const f2Std = new Date(f2.schedule.std).getTime();

  return f1Std - f2Std;
};

export const hasOpenTasks = (tasks?: Task[]) => (tasks ?? []).some(task => task.status === 'OPEN');

export const getOpenTaskChannelId = (tasks?: Task[]): string | undefined =>
  (tasks ?? [])
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .find(task => task.status === 'OPEN' && task.channelId)?.channelId;

export const getOpenTaskMessageId = (tasks?: Task[]): string | undefined =>
  (tasks ?? []).find(task => task.status === 'OPEN')?.messageId;

export const getFlightRoomStatus = (flightRoom: FlightRoomSummary) => {
  let status = FlightStatus.ACTIVE;

  if (hasOpenTasks(flightRoom.tasks)) {
    status = FlightStatus.TASKS;
  } else if (isRoomLanded(flightRoom)) {
    status = FlightStatus.LANDED;
  }

  return status;
};
